import { useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useCallback, useMemo } from 'react';

import { type UUID } from '~/types/common';

import {
  type ContractListItem,
  type ContractStatus,
  fetchContract,
  fetchContracts,
  queryKeysContract,
} from '~/data/contract';

import { getContractNumberLabel } from '~/modules/contract/utils/getContractNumberLabel';

import { OptionPrimarySecondary } from '@vestigas/ui/molecules';

import { type Option, SelectServerDriven } from './SelectServerDriven';
import { createGetItemData, createLoadOptions } from './utils';

/**
 * Creates a display label for a contract (used for search/filter).
 * Shows the buyer-assigned ID (or the "no contract number" placeholder) along with the seller company name.
 */
const getOptionLabel = (contract: ContractListItem) => {
  const contractId = getContractNumberLabel(contract.buyerAssignedId);
  const companyName = contract.sellerCompanyName || contract.buyerCompanyName;

  return companyName ? `${contractId} - ${companyName}` : contractId;
};

/**
 * Formats select options like
 * ----------------------
 * <buyerAssignedId>
 * <contractName or companyNames>
 * ----------------------
 *
 * Note: contractName is only available when fetching individual contracts (Contract type),
 * not when loading from the list (ContractListItem type). Falls back to company names.
 */
export const contractBuyerOptionLabel = ({
  data,
}: Option<ContractListItem>) => {
  // contractName is available on Contract but not ContractListItem, using optional access
  const contractName =
    (data as { contractName?: string }).contractName ||
    [data.sellerCompanyName, data.buyerCompanyName].filter(Boolean).join(' ↔ ');

  return (
    <OptionPrimarySecondary
      primaryInformation={getContractNumberLabel(data.buyerAssignedId)}
      secondaryInformation={contractName}
    />
  );
};

/**
 * Formats select options like
 * ----------------------
 * <buyerAssignedId>
 * <sellerCompanyName>
 * ----------------------
 */
export const buyerIdSellerNameOptionLabel = ({
  data,
}: Option<ContractListItem>) => {
  return (
    <OptionPrimarySecondary
      primaryInformation={getContractNumberLabel(data.buyerAssignedId)}
      secondaryInformation={data.sellerCompanyName ?? undefined}
    />
  );
};

type P = {
  readonly buyerCompanyId?: UUID;
  readonly formatOptionLabel?: (option: Option<ContractListItem>) => ReactNode;
  readonly isClearable?: boolean;
  readonly isDisabled?: boolean;
  readonly isMultiSelect?: boolean;
  readonly isRemovableValue?: (data: Option<ContractListItem>) => boolean;
  readonly onChange: (
    value: Array<{ id: UUID }> | { id: UUID },
    data: ContractListItem[] | ContractListItem,
  ) => void;
  readonly placeholder?: string;
  readonly sellerCompanyId?: UUID;
  readonly status?: ContractStatus[];
  readonly value: UUID[];
} & ComponentStyling;

export const MultiSelectContracts = ({
  buyerCompanyId,
  className,
  formatOptionLabel = contractBuyerOptionLabel,
  isClearable = true,
  isDisabled = false,
  isMultiSelect = true,
  isRemovableValue,
  onChange,
  placeholder = 'Verträge',
  sellerCompanyId,
  status,
  style,
  value,
}: P) => {
  const queryClient = useQueryClient();

  // Memoize filter params to ensure stable reference for loadOptions
  const filterParams = useMemo(
    () => ({
      buyerCompanyId,
      // Only contracts with a customer contract number can be picked.
      hasBuyerAssignedId: true,
      sellerCompanyId,
      status,
    }),
    [buyerCompanyId, sellerCompanyId, status],
  );

  /**
   * Loads the options for the multi-select dropdown.
   * @param {string} searchString - The search string entered by the user.
   * @param {unknown} loadedOptions - The already loaded options.
   * @param {{ page: number }} - The current page number.
   */
  const loadOptions = createLoadOptions<ContractListItem>({
    createQueryKey: ({ page, ...queryParams }) =>
      queryKeysContract.getAll({ ...queryParams, ...filterParams }),
    async fetchItems({ page, ...queryParams }) {
      const response = await fetchContracts(
        {
          ...queryParams,
          ...filterParams,
          searchString: queryParams.searchString,
        },
        {},
      );

      return {
        data: response?.data ?? [],
        hasNextPage: response?.hasNextPage ?? false,
      };
    },
    getOptionLabel,
    queryClient,
  });

  /**
   * Gets contract data for a contract with a given contractId.
   * Attempts to get it from the query cache and fetches it from the API
   * if the contract data is not in the cache.
   * @param {string} contractId - The ID of the contract to get.
   */
  const getItemData = createGetItemData<ContractListItem>({
    createQueryKey: (contractId) => queryKeysContract.get(contractId),
    fetchItem: fetchContract,
    queryClient,
    queryKeyBase: [queryKeysContract.base()],
  });

  const handleChange = useCallback(
    (
      newValues: string[] | string,
      data: ContractListItem[] | ContractListItem,
    ) => {
      onChange(
        Array.isArray(newValues)
          ? newValues.map((id) => ({ id }))
          : { id: newValues },
        data,
      );
    },
    [onChange],
  );

  return (
    <SelectServerDriven<ContractListItem>
      className={className}
      formatOptionLabel={formatOptionLabel}
      getItemData={getItemData}
      getOptionLabel={getOptionLabel}
      isClearable={isClearable}
      isDisabled={isDisabled}
      isMultiSelect={isMultiSelect}
      isRemovableValue={isRemovableValue}
      loadOptions={loadOptions}
      placeholder={placeholder}
      style={style}
      value={value}
      onChange={handleChange}
    />
  );
};
